import React from 'react';
import { Dumbbell, Pill, Leaf, HeartPulse, Layers, ArrowRight } from 'lucide-react';

interface ProductsProps {
    onNavigate?: (page: 'home' | 'blog' | 'contact' | 'resources' | 'products', sectionId?: string) => void;
}

const Products: React.FC<ProductsProps> = ({ onNavigate }) => {
  const categories = [
    {
      title: "Sports Nutrition & Performance",
      icon: <Dumbbell className="w-8 h-8" />,
      desc: "Power your customers' fitness goals with scientifically formulated nutrition.",
      items: ["Whey Protein (Isolate, Concentrate)", "Plant-Based & Vegan Proteins", "Pre-Workout & BCAAs", "Creatine & Glutamine", "Mass Gainers"],
      color: "orange"
    },
    {
      title: "Vitamins & Minerals",
      icon: <Pill className="w-8 h-8" />,
      desc: "Essential nutrition for everyday wellness and targeted health support.",
      items: ["Multivitamins (Men, Women, Kids)", "Vitamin D3, B12, C, E", "Calcium, Iron, Zinc, Magnesium", "Omega-3 & Fish Oil", "Biotin & Collagen"],
      color: "black"
    },
    {
      title: "Ayurvedic & Herbal",
      icon: <Leaf className="w-8 h-8" />,
      desc: "Traditional Indian wellness wisdom meets modern GMP manufacturing.",
      items: ["Ashwagandha (KSM-66, Extract)", "Turmeric & Curcumin", "Shilajit & Gokshura", "Triphala, Giloy, Brahmi", "Supergreen Blends"],
      color: "orange"
    },
    {
      title: "Functional Wellness",
      icon: <HeartPulse className="w-8 h-8" />,
      desc: "Targeted solutions for specific health concerns and modern lifestyles.",
      items: ["Probiotics & Digestive Health", "Immunity Boosters", "Sleep Support (Melatonin, Valerian)", "Stress & Mental Focus", "Hair, Skin & Nail Formulas"],
      color: "black"
    }
  ];

  const handleMoreClick = (e: React.MouseEvent) => {
      if (onNavigate) {
          e.preventDefault();
          onNavigate('products');
      }
  };

  return (
    <section id="products" className="py-24 bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-orange-50 border border-orange-100 mb-6">
            <span className="text-orange-800 text-xs font-bold uppercase tracking-widest">
                Our Capabilities
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            What We Manufacture
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            From Ayurvedic wisdom to sports nutrition science—we manufacture supplements for every health goal.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-10 mb-12">
          {categories.map((cat, idx) => (
            <div key={idx} className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group border border-gray-100 flex flex-col h-full">
              {/* Slick Left Frame */}
              <div className={`absolute top-0 bottom-0 left-0 w-1.5 ${cat.color === 'orange' ? 'bg-orange-500' : 'bg-black'} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
              
              <div className="flex items-start mb-8">
                <div className={`p-4 rounded-2xl shadow-sm mr-6 border transition-transform group-hover:scale-110 duration-300 ${
                    cat.color === 'orange' 
                    ? 'bg-orange-50 border-orange-100 text-orange-600' 
                    : 'bg-gray-50 border-gray-200 text-gray-900'
                }`}>
                  {React.cloneElement(cat.icon as React.ReactElement<any>, { strokeWidth: 1.5 })}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{cat.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{cat.desc}</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 mt-auto">
                 <ul className="grid sm:grid-cols-1 gap-3">
                    {cat.items.map((item, i) => (
                        <li key={i} className="flex items-center text-sm font-medium text-gray-700">
                            <span className={`w-1.5 h-1.5 rounded-full mr-3 ${cat.color === 'orange' ? 'bg-orange-500' : 'bg-black'}`}></span>
                            {item}
                        </li>
                    ))}
                 </ul>
              </div>
            </div>
          ))}
        </div>

        {/* More CTA */}
        <div className="text-center mb-20">
            <a 
                href="/products" 
                onClick={handleMoreClick}
                className="inline-flex items-center text-lg font-bold text-orange-800 hover:text-orange-900 transition-colors group border-b-2 border-transparent hover:border-orange-600 pb-0.5"
            >
                More Products <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
        </div>

        {/* Formats Strip */}
        <div className="relative rounded-3xl overflow-hidden">
             <div className="absolute inset-0 bg-black"></div>
             <div className="absolute -left-20 -top-20 w-64 h-64 bg-orange-600/20 rounded-full blur-3xl"></div>
             <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-orange-600/20 rounded-full blur-3xl"></div>

            <div className="relative z-10 p-10 sm:p-14 text-center text-white">
                 <h3 className="text-2xl font-bold mb-8">Available Formats</h3>
                <div className="flex flex-wrap justify-center gap-4 md:gap-8 lg:gap-12">
                     <div className="flex items-center bg-gray-900/50 px-5 py-3 rounded-full border border-gray-800 backdrop-blur-sm hover:border-orange-500/50 transition-colors">
                        <Layers className="w-5 h-5 mr-3 text-orange-500" /> 
                        <span className="font-bold">Powders</span>
                     </div>
                     <div className="flex items-center bg-gray-900/50 px-5 py-3 rounded-full border border-gray-800 backdrop-blur-sm hover:border-orange-500/50 transition-colors">
                        <Layers className="w-5 h-5 mr-3 text-orange-500" /> 
                        <span className="font-bold">Capsules</span>
                     </div>
                     <div className="flex items-center bg-gray-900/50 px-5 py-3 rounded-full border border-gray-800 backdrop-blur-sm hover:border-orange-500/50 transition-colors">
                        <Layers className="w-5 h-5 mr-3 text-orange-500" /> 
                        <span className="font-bold">Tablets</span>
                     </div>
                     <div className="flex items-center bg-gray-900/50 px-5 py-3 rounded-full border border-gray-800 backdrop-blur-sm hover:border-orange-500/50 transition-colors">
                        <Layers className="w-5 h-5 mr-3 text-orange-500" /> 
                        <span className="font-bold">Softgels</span>
                     </div>
                     <div className="flex items-center bg-gray-900/50 px-5 py-3 rounded-full border border-gray-800 backdrop-blur-sm hover:border-orange-500/50 transition-colors">
                        <Layers className="w-5 h-5 mr-3 text-orange-500" /> 
                        <span className="font-bold">Gummies</span>
                     </div>
                </div>
                <p className="mt-8 text-gray-300">We manufacture supplements in the format that works best for your brand.</p>
            </div>
        </div>
      </div>
    </section>
  );
};

export default Products;